import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  ogImage?: string;
  schema?: object;
  canonical?: string;
}

export function useSEO({ title, description, keywords, ogImage, schema, canonical }: SEOProps) {
  useEffect(() => {
    // Set title
    document.title = title;

    // Set or update meta tags
    const setMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Basic meta tags
    setMetaTag('description', description);
    setMetaTag('keywords', keywords);

    // Open Graph tags
    setMetaTag('og:title', title, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:url', canonical || window.location.href, true);
    
    if (ogImage) {
      setMetaTag('og:image', ogImage, true);
    }

    // Twitter tags
    setMetaTag('twitter:title', title);
    setMetaTag('twitter:description', description);
    
    if (ogImage) {
      setMetaTag('twitter:image', ogImage);
    }

    // Canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonical || window.location.href;

    // Schema.org JSON-LD
    if (schema) {
      let schemaScript = document.querySelector('script[type="application/ld+json"]');
      
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.type = 'application/ld+json';
        document.head.appendChild(schemaScript);
      }
      
      schemaScript.textContent = JSON.stringify(schema);
    }

    // Cleanup function
    return () => {
      // Remove schema script on unmount
      const schemaScript = document.querySelector('script[type="application/ld+json"]');
      if (schemaScript) {
        schemaScript.remove();
      }
    };
  }, [title, description, keywords, ogImage, schema, canonical]);
}

// Helper function to generate organization schema
export const getOrganizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'LegalService',
  name: 'Yıldız & Başyiğit Hukuk ve Danışmanlık',
  image: 'https://static.readdy.ai/image/07f91bd9f2ad3b84149b39a4e0627797/04e25c63716513028a093e30d78ebbd8.png',
  '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`,
  url: import.meta.env.VITE_SITE_URL || 'https://example.com',
  telephone: '0505 605 58 45',
  email: 'info@ybhukukdanismanlik.com',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Çelebiler Mah. 119. Cad. No : 1 D : 6',
    addressLocality: 'Merkez',
    addressRegion: 'Isparta',
    postalCode: '32000',
    addressCountry: 'TR'
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 37.7648,
    longitude: 30.5566
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '18:00'
    },
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: 'Saturday',
      opens: '09:00',
      closes: '13:00'
    }
  ],
  priceRange: '$$',
  areaServed: {
    '@type': 'City',
    name: 'Isparta'
  }
});

// Helper function to generate breadcrumb schema
export const getBreadcrumbSchema = (items: { name: string; url: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: item.url
  }))
});

// Helper function to generate article schema
export const getArticleSchema = (article: {
  title: string;
  description: string;
  image: string;
  datePublished: string;
  dateModified: string;
  author: string;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: article.title,
  description: article.description,
  image: article.image,
  datePublished: article.datePublished,
  dateModified: article.dateModified,
  author: {
    '@type': 'Organization',
    name: article.author
  },
  publisher: {
    '@type': 'Organization',
    name: 'Yıldız & Başyiğit Hukuk ve Danışmanlık',
    logo: {
      '@type': 'ImageObject',
      url: 'https://static.readdy.ai/image/07f91bd9f2ad3b84149b39a4e0627797/04e25c63716513028a093e30d78ebbd8.png'
    }
  }
});

// Helper function to generate service schema
export const getServiceSchema = (service: {
  name: string;
  description: string;
  url: string;
}) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: service.name,
  description: service.description,
  provider: {
    '@type': 'LegalService',
    name: 'Yıldız & Başyiğit Hukuk ve Danışmanlık',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Çelebiler Mah. 119. Cad. No : 1 D : 6',
      addressLocality: 'Merkez',
      addressRegion: 'Isparta',
      addressCountry: 'TR'
    }
  },
  areaServed: {
    '@type': 'City',
    name: 'Isparta'
  },
  url: service.url
});
